import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  deletePrintProduct, deleteProductPhoto, quotePrintProduct, savePrintProduct, setPricingSettingsRemote, uploadProductPhoto
} from '../../../services/adminApi';
import useConfirm from '../shell/useConfirm';
import ChangeHistory from './ChangeHistory';
import { Drawer, Switch, Tag } from './ui';
import {
  FICHE_VIDE, brouillonModifie, centsToEuros, eurosToCents, formatEuros, margePourcent, prixConseille, coutReel, portConseille, TVA_FOURNISSEUR_PCT, versBrouillon, versPayload, versTarifHistorique
} from './productForm';

// FICHE D'UN PRODUIT : le SEUL endroit ou se regle le prix d'un produit — coût, marge, prix de vente et port — pour TOUS les produits.
//  - produit du catalogue : tout est modifiable ici ;
//  - format historique Gelato (Livret, Standard, Premium) : son prix de vente (base et par 2 pages) se regle ici aussi ; le reste
//    (coût, port, pages) vient du code et s'affiche en lecture seule.
// La clé d'API n'apparait nulle part : elle vient de l'environnement.

const SLOTS = ['cover', 'open', 'spine', 'edge'];

function Champ({ label, aide, children, wide = false }) {
  return (
    <label className={`adm-field${wide ? ' is-wide' : ''}`}>
      <span>{label}</span>
      {children}
      {aide && <small>{aide}</small>}
    </label>
  );
}

export default function ProductDrawer({ produit, fournisseurs, onClose, onSaved }) {
  const { t } = useTranslation('admin');
  const nouveau = !produit;
  const historique = Boolean(produit && produit.builtin);
  const [courant, setCourant] = useState(produit);
  const [formatId, setFormatId] = useState('');
  const [b, setB] = useState(() => (produit ? versBrouillon(produit) : { ...FICHE_VIDE }));
  const [erreur, setErreur] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);
  const [verification, setVerification] = useState(null); // 'en cours' | { ... } | { erreur }
  const [pays, setPays] = useState(() => Object.keys((produit && produit.shipping) || {})[0] || 'FR');
  const [pagesDevis, setPagesDevis] = useState(() => String((produit && produit.basePages) || 30));
  const [photoEnCours, setPhotoEnCours] = useState('');
  const [confirmer, confirmation] = useConfirm();

  const maj = (champ, valeur) => setB((prec) => ({ ...prec, [champ]: valeur }));
  const majPort = (index, champ, valeur) => setB((prec) => ({ ...prec, shipping: prec.shipping.map((l, i) => (i === index ? { ...l, [champ]: valeur } : l)) }));

  const coutBase = eurosToCents(b.costBase);
  const coutPar2 = eurosToCents(b.costPer2Pages);
  const prixBase = eurosToCents(b.priceBase);
  const prixPar2 = eurosToCents(b.pricePer2Pages);
  const marge = Number(b.marginPercent);
  const terminaison = b.roundingEnding === '' ? 90 : Number(b.roundingEnding);
  const margeBase = margePourcent(prixBase, coutReel(coutBase));
  const modifie = useMemo(() => brouillonModifie(produit, b), [produit, b]);
  const fournisseur = fournisseurs.find((f) => f.id === b.providerId);
  const lecture = (valeur) => <span className="adm-readonly">{valeur === '' || valeur === null || valeur === undefined ? '—' : valeur}</span>;

  const demanderFermeture = async () => {
    if (modifie && !(await confirmer({
      title: t('catalog.drawer.discardTitle'),
      message: t('catalog.drawer.discardBody'),
      confirmLabel: t('catalog.drawer.discardYes')
    }))) return;
    onClose();
  };

  const appliquerMarge = () => {
    const c = prixConseille({ coutBaseCents: coutBase, coutPar2Cents: coutPar2, marge, terminaison });
    setB((prec) => ({
      ...prec,
      priceBase: c.base === null ? prec.priceBase : centsToEuros(c.base),
      pricePer2Pages: c.par2 === null ? prec.pricePer2Pages : centsToEuros(c.par2)
    }));
  };

  const enregistrer = async () => {
    setErreur('');
    if (Number.isNaN(prixBase) || Number.isNaN(prixPar2)) { setErreur(t('catalog.drawer.errors.price')); return; }
    setEnregistrement(true);
    try {
      let id = produit && produit.formatId;
      if (historique) {
        await setPricingSettingsRemote(versTarifHistorique(produit.formatId, b));
      } else {
        id = nouveau ? formatId.trim().toLowerCase() : produit.formatId;
        if (!id) { setErreur(t('catalog.drawer.errors.formatId')); return; }
        await savePrintProduct(id, versPayload(b));
      }
      await onSaved(t('catalog.drawer.saved', { name: id }));
    } catch (err) {
      setErreur(err?.message || t('catalog.drawer.errors.save'));
    } finally {
      setEnregistrement(false);
    }
  };

  const supprimer = async () => {
    const ok = await confirmer({
      title: t('catalog.drawer.deleteTitle', { name: produit.formatId }),
      message: t('catalog.drawer.deleteBody'),
      confirmLabel: t('catalog.drawer.deleteYes')
    });
    if (!ok) return;
    try {
      await deletePrintProduct(produit.formatId);
      await onSaved(t('catalog.drawer.deleted', { name: produit.formatId }));
    } catch (err) {
      setErreur(err?.message || t('catalog.drawer.errors.delete'));
    }
  };

  const verifier = async () => {
    setVerification('en cours');
    try {
      setVerification(await quotePrintProduct(produit.formatId, { country: pays, pageCount: Number(pagesDevis) || undefined }));
    } catch (err) {
      setVerification({ erreur: err?.message || t('catalog.drawer.check.failed') });
    }
  };

  const adopterLeCout = () => {
    if (!verification || !verification.real) return;
    setB((prec) => {
      const port = verification.real.shippingCostCents;
      return {
        ...prec,
        costBase: verification.real.productCostCents === null || verification.real.productCostCents === undefined ? prec.costBase : centsToEuros(verification.real.productCostCents),
        shipping: prec.shipping.map((l) => (l.country.toUpperCase() === verification.country && Number.isInteger(port) ? { ...l, cost: centsToEuros(port) } : l))
      };
    });
  };

  const televerser = async (slot, fichier) => {
    if (!fichier) return;
    setPhotoEnCours(slot);
    setErreur('');
    try {
      setCourant((await uploadProductPhoto(produit.formatId, slot, fichier)).product);
    } catch (err) {
      setErreur(err?.message || t('catalog.drawer.errors.photo'));
    } finally {
      setPhotoEnCours('');
    }
  };

  const retirer = async (slot) => {
    setPhotoEnCours(slot);
    setErreur('');
    try {
      setCourant((await deleteProductPhoto(produit.formatId, slot)).product);
    } catch (err) {
      setErreur(err?.message || t('catalog.drawer.errors.photo'));
    } finally {
      setPhotoEnCours('');
    }
  };

  const titre = nouveau ? t('catalog.drawer.newTitle') : (produit.name.fr || produit.formatId);
  const actif = b.visibility !== 'off';
  const photos = (courant && courant.extra && courant.extra.photos) || {};
  const peutVerifier = !nouveau && fournisseur && fournisseur.supportsQuote;

  return (
    <Drawer title={titre} onRequestClose={demanderFermeture}>
      {confirmation}

      {/* ---- Mise en vente ---- */}
      <section className="adm-dsec">
        <h3>{t('catalog.drawer.sections.sale')}</h3>
        {historique ? (
          <p className="adm-note"><Tag tone="ok">{t('catalog.states.public')}</Tag> {t('catalog.drawer.builtinNote')}</p>
        ) : (
          <>
            <div className="adm-switchrow">
              <Switch checked={actif} onChange={(v) => maj('visibility', v ? 'testers' : 'off')} label={t('catalog.drawer.active')} />
              <span>{actif ? t('catalog.drawer.activeOn') : t('catalog.drawer.activeOff')}</span>
            </div>
            {actif && (
              <div className="adm-seg" role="group" aria-label={t('catalog.drawer.visibleBy')}>
                <button type="button" aria-pressed={b.visibility === 'testers'} onClick={() => maj('visibility', 'testers')}>{t('catalog.states.testers')}</button>
                <button type="button" aria-pressed={b.visibility === 'public'} onClick={() => maj('visibility', 'public')}>{t('catalog.states.public')}</button>
              </div>
            )}
            <p className="adm-note">{t('catalog.drawer.saleHelp')}</p>
          </>
        )}
      </section>

      {/* ---- Coût, marge et prix : l'unique endroit ---- */}
      <section className="adm-dsec">
        <h3>{t('catalog.drawer.sections.money')}</h3>
        {historique && <p className="adm-note">{t('catalog.drawer.builtinPriceNote')}</p>}
        <div className="adm-form">
          <Champ label={t('catalog.drawer.fields.costBase')}>
            {historique ? lecture(formatEuros(coutBase)) : <input className="adm-input" inputMode="decimal" value={b.costBase} onChange={(e) => maj('costBase', e.target.value)} />}
          </Champ>
          <Champ label={t('catalog.drawer.fields.costPer2')}>
            {historique ? lecture(formatEuros(coutPar2)) : <input className="adm-input" inputMode="decimal" value={b.costPer2Pages} onChange={(e) => maj('costPer2Pages', e.target.value)} />}
          </Champ>
          {!historique && (
            <>
              <Champ label={t('catalog.drawer.fields.margin')} aide={t('catalog.drawer.fields.marginHelp')}>
                <input className="adm-input" inputMode="decimal" value={b.marginPercent} onChange={(e) => maj('marginPercent', e.target.value)} />
              </Champ>
              <Champ label={t('catalog.drawer.fields.ending')} aide={t('catalog.drawer.fields.endingHelp')}>
                <input className="adm-input" type="number" min="0" max="99" value={b.roundingEnding} onChange={(e) => maj('roundingEnding', e.target.value)} />
              </Champ>
            </>
          )}
          <Champ label={t('catalog.drawer.fields.priceBase')}>
            <input className="adm-input" inputMode="decimal" value={b.priceBase} onChange={(e) => maj('priceBase', e.target.value)} />
          </Champ>
          <Champ label={t('catalog.drawer.fields.pricePer2')}>
            <input className="adm-input" inputMode="decimal" value={b.pricePer2Pages} onChange={(e) => maj('pricePer2Pages', e.target.value)} />
          </Champ>
        </div>
        <div className="adm-price" aria-live="polite">
          <span>{t('catalog.drawer.salePrice')}</span>
          <strong>{formatEuros(prixBase)}</strong>
          <span>{margeBase === null ? t('catalog.drawer.marginUnknown') : t('catalog.drawer.marginNow', { amount: formatEuros(prixBase - coutReel(coutBase)), percent: margeBase })}</span>
        </div>
        {!historique && (
          <div className="adm-actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={appliquerMarge} disabled={!(marge > 0)}>{t('catalog.drawer.applyMargin')}</button>
          </div>
        )}
        <p className="adm-note">{t('catalog.drawer.perPageNote', { pages: b.basePages })}</p>
        <p className="adm-note">{t('catalog.drawer.vatNote', { pct: TVA_FOURNISSEUR_PCT })}</p>
      </section>

      {/* ---- Livraison ---- */}
      <section className="adm-dsec">
        <h3>{t('catalog.drawer.sections.shipping')}</h3>
        {historique ? (
          <>
            <p className="adm-note">{t('catalog.drawer.builtinShipping')}</p>
            <ul className="adm-plain">
              {Object.entries(produit.shipping || {}).map(([code, l]) => <li key={code}><b>{code}</b> · {formatEuros(l.priceCents)}</li>)}
            </ul>
          </>
        ) : (
          <>
            <p className="adm-note">{t('catalog.drawer.shippingHelp')}</p>
            <div className="adm-tbl">
              <table>
                <thead><tr><th>{t('catalog.drawer.shippingCols.country')}</th><th>{t('catalog.drawer.shippingCols.price')}</th><th>{t('catalog.drawer.shippingCols.cost')}</th><th /></tr></thead>
                <tbody>
                  {b.shipping.map((ligne, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td><input className="adm-input" style={{ width: '4.5rem' }} maxLength={2} aria-label={t('catalog.drawer.shippingCols.country')} value={ligne.country} onChange={(e) => majPort(index, 'country', e.target.value.toUpperCase())} /></td>
                      <td><input className="adm-input" style={{ width: '6rem' }} inputMode="decimal" aria-label={t('catalog.drawer.shippingCols.price')} value={ligne.price} onChange={(e) => majPort(index, 'price', e.target.value)} /></td>
                      <td><input className="adm-input" style={{ width: '6rem' }} inputMode="decimal" aria-label={t('catalog.drawer.shippingCols.cost')} value={ligne.cost} onChange={(e) => majPort(index, 'cost', e.target.value)} /></td>
                      <td><button type="button" className="btn btn-outline btn-sm" onClick={() => setB((prec) => ({ ...prec, shipping: prec.shipping.filter((_, i) => i !== index) }))}>{t('catalog.drawer.remove')}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adm-actions">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setB((prec) => ({ ...prec, shipping: [...prec.shipping, { country: '', price: '', cost: '' }] }))}>{t('catalog.drawer.addCountry')}</button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setB((prec) => ({ ...prec, shipping: prec.shipping.map((l) => { const p = portConseille(eurosToCents(l.cost)); return p === null || Number.isNaN(p) ? l : { ...l, price: centsToEuros(p) }; }) }))}
              >
                {t('catalog.drawer.shippingAtCost', { pct: TVA_FOURNISSEUR_PCT })}
              </button>
            </div>
            <Champ label={t('catalog.drawer.freeShipping')} aide={t('catalog.drawer.freeShippingHelp')}>
              <input className="adm-input" style={{ maxWidth: '9rem' }} inputMode="decimal" value={b.freeShippingThreshold} onChange={(e) => maj('freeShippingThreshold', e.target.value)} />
            </Champ>
          </>
        )}
      </section>

      {/* ---- Vérifier avec le fournisseur ---- */}
      {!nouveau && (
        <section className="adm-dsec">
          <h3>{t('catalog.drawer.sections.check')}</h3>
          {peutVerifier ? (
            <>
              <div className="adm-form">
                <Champ label={t('catalog.drawer.check.country')}>
                  <input className="adm-input" maxLength={2} value={pays} onChange={(e) => setPays(e.target.value.toUpperCase())} />
                </Champ>
                <Champ label={t('catalog.drawer.check.pages')}>
                  <input className="adm-input" type="number" value={pagesDevis} onChange={(e) => setPagesDevis(e.target.value)} />
                </Champ>
              </div>
              <div className="adm-actions">
                <button type="button" className="btn btn-outline btn-sm" onClick={verifier} disabled={verification === 'en cours'}>
                  {verification === 'en cours' ? t('catalog.drawer.check.running') : t('catalog.drawer.check.run', { provider: fournisseur.label })}
                </button>
              </div>
              <div className="adm-note" role="status" aria-live="polite">
                {verification && verification.erreur && <span className="adm-error">{verification.erreur}</span>}
                {verification && verification.real && (() => {
                  const dp = verification.diff.productCostCents;
                  const ds = verification.diff.shippingCostCents;
                  const conforme = !dp && !ds;
                  return (
                    <>
                      <Tag tone={conforme ? 'ok' : 'warn'}>{conforme ? t('catalog.drawer.check.same') : t('catalog.drawer.check.gap')}</Tag>{' '}
                      {t('catalog.drawer.check.result', {
                        provider: fournisseur.label,
                        productReal: formatEuros(verification.real.productCostCents),
                        productStored: formatEuros(verification.stored.productCostCents),
                        shippingReal: formatEuros(verification.real.shippingCostCents),
                        shippingStored: formatEuros(verification.stored.shippingCostCents)
                      })}
                      {!conforme && (
                        <div className="adm-actions">
                          <button type="button" className="btn btn-outline btn-sm" onClick={adopterLeCout}>{t('catalog.drawer.check.adopt')}</button>
                          <small>{t('catalog.drawer.check.adoptHelp')}</small>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          ) : (
            <p className="adm-note">{historique ? t('catalog.drawer.check.noneGelato') : t('catalog.drawer.check.none')}</p>
          )}
        </section>
      )}

      {/* ---- Détails du produit ---- */}
      <details className="adm-dsec adm-details">
        <summary><h3>{t('catalog.drawer.sections.details')}</h3></summary>
        <div className="adm-form">
          {nouveau && (
            <Champ label={t('catalog.drawer.fields.formatId')} aide={t('catalog.drawer.fields.formatIdHelp')} wide>
              <input className="adm-input" value={formatId} onChange={(e) => setFormatId(e.target.value)} />
            </Champ>
          )}
          <Champ label={t('catalog.drawer.fields.provider')}>
            {historique ? lecture(produit.providerId) : (
              <select className="adm-input" value={b.providerId} onChange={(e) => maj('providerId', e.target.value)}>
                <option value="">{t('catalog.drawer.choose')}</option>
                {fournisseurs.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            )}
          </Champ>
          <Champ label={t('catalog.drawer.fields.sku')}>
            {historique ? lecture(b.sku) : <input className="adm-input" value={b.sku} onChange={(e) => maj('sku', e.target.value)} />}
          </Champ>
          <Champ label={t('catalog.drawer.fields.nameFr')}>{historique ? lecture(b.nameFr) : <input className="adm-input" value={b.nameFr} onChange={(e) => maj('nameFr', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.nameEn')}>{historique ? lecture(b.nameEn) : <input className="adm-input" value={b.nameEn} onChange={(e) => maj('nameEn', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.taglineFr')}>{historique ? lecture(b.taglineFr) : <input className="adm-input" value={b.taglineFr} onChange={(e) => maj('taglineFr', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.taglineEn')}>{historique ? lecture(b.taglineEn) : <input className="adm-input" value={b.taglineEn} onChange={(e) => maj('taglineEn', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.cover')}>
            {historique ? lecture(b.coverType === 'hard' ? t('catalog.drawer.coverHard') : t('catalog.drawer.coverSoft')) : (
              <select className="adm-input" value={b.coverType} onChange={(e) => maj('coverType', e.target.value)}>
                <option value="hard">{t('catalog.drawer.coverHard')}</option>
                <option value="soft">{t('catalog.drawer.coverSoft')}</option>
              </select>
            )}
          </Champ>
          <Champ label={t('catalog.drawer.fields.binding')}>{historique ? lecture(b.binding) : <input className="adm-input" value={b.binding} onChange={(e) => maj('binding', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.orientation')}>
            {historique ? lecture(b.orientation) : (
              <select className="adm-input" value={b.orientation} onChange={(e) => maj('orientation', e.target.value)}>
                <option value="portrait">{t('catalog.drawer.orient.portrait')}</option>
                <option value="landscape">{t('catalog.drawer.orient.landscape')}</option>
                <option value="square">{t('catalog.drawer.orient.square')}</option>
              </select>
            )}
          </Champ>
          <Champ label={t('catalog.drawer.fields.renderStyle')} aide={t('catalog.drawer.fields.renderStyleHelp')}>
            {historique ? lecture(b.styleProfile) : (
              <select className="adm-input" value={b.styleProfile} onChange={(e) => maj('styleProfile', e.target.value)}>
                <option value="livret">{t('catalog.drawer.style.livret')}</option>
                <option value="standard">{t('catalog.drawer.style.standard')}</option>
                <option value="luxe">{t('catalog.drawer.style.luxe')}</option>
              </select>
            )}
          </Champ>
          <Champ label={t('catalog.drawer.fields.trimWidth')}>{historique ? lecture(b.trimWidthMm) : <input className="adm-input" inputMode="decimal" value={b.trimWidthMm} onChange={(e) => maj('trimWidthMm', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.trimHeight')}>{historique ? lecture(b.trimHeightMm) : <input className="adm-input" inputMode="decimal" value={b.trimHeightMm} onChange={(e) => maj('trimHeightMm', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.safeMargin')}>{historique ? lecture(b.safeMarginMm) : <input className="adm-input" inputMode="decimal" value={b.safeMarginMm} onChange={(e) => maj('safeMarginMm', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.minPages')}>{historique ? lecture(b.minPages) : <input className="adm-input" type="number" value={b.minPages} onChange={(e) => maj('minPages', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.maxPages')}>{historique ? lecture(b.maxPages) : <input className="adm-input" type="number" value={b.maxPages} onChange={(e) => maj('maxPages', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.pageStep')} aide={t('catalog.drawer.fields.pageStepHelp')}>{historique ? lecture(b.pageStep) : <input className="adm-input" type="number" value={b.pageStep} onChange={(e) => maj('pageStep', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.basePages')} aide={t('catalog.drawer.fields.basePagesHelp')}>{historique ? lecture(b.basePages) : <input className="adm-input" type="number" value={b.basePages} onChange={(e) => maj('basePages', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.paperFr')}>{historique ? lecture(b.paperFr) : <input className="adm-input" value={b.paperFr} onChange={(e) => maj('paperFr', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.paperEn')}>{historique ? lecture(b.paperEn) : <input className="adm-input" value={b.paperEn} onChange={(e) => maj('paperEn', e.target.value)} />}</Champ>
          <Champ label={t('catalog.drawer.fields.sortOrder')} aide={t('catalog.drawer.fields.sortOrderHelp')}>{historique ? lecture(b.sortOrder) : <input className="adm-input" type="number" value={b.sortOrder} onChange={(e) => maj('sortOrder', e.target.value)} />}</Champ>
          {!historique && (
            <label className="adm-check">
              <input type="checkbox" checked={b.recommended} onChange={(e) => maj('recommended', e.target.checked)} /> {t('catalog.drawer.fields.recommended')}
            </label>
          )}
        </div>
      </details>

      {/* ---- Photos ---- */}
      {!nouveau && (
        <section className="adm-dsec">
          <h3>{t('catalog.drawer.sections.photos')}</h3>
          {historique ? (
            <p className="adm-note">{t('catalog.drawer.photosBuiltin')}</p>
          ) : (
            <>
              <div className="adm-gal">
                {SLOTS.map((slot) => (
                  <div key={slot} className={`adm-gal-slot${photos[slot] ? ' is-filled' : ''}`}>
                    {photos[slot] ? <img src={photos[slot].url} alt={t(`catalog.drawer.photoSlots.${slot}`)} loading="lazy" /> : <span>{t(`catalog.drawer.photoSlots.${slot}`)}</span>}
                    <div className="adm-gal-actions">
                      <label className="btn btn-outline btn-sm">
                        {photoEnCours === slot ? t('catalog.drawer.photoUploading') : (photos[slot] ? t('catalog.drawer.photoReplace') : t('catalog.drawer.photoAdd'))}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="adm-sr" disabled={Boolean(photoEnCours)} onChange={(e) => { televerser(slot, e.target.files && e.target.files[0]); e.target.value = ''; }} />
                      </label>
                      {photos[slot] && <button type="button" className="btn btn-outline btn-sm" disabled={Boolean(photoEnCours)} onClick={() => retirer(slot)}>{t('catalog.drawer.remove')}</button>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="adm-note">{t('catalog.drawer.photosHelp')}</p>
            </>
          )}
        </section>
      )}

      {/* ---- Historique ---- */}
      {!nouveau && (
        <section className="adm-dsec">
          <h3>{t('catalog.drawer.sections.history')}</h3>
          <ChangeHistory area={historique ? 'pricing' : 'product'} keyId={historique ? 'global' : produit.formatId} limit={20} />
        </section>
      )}

      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      <div className="adm-drawer-foot">
        <button type="button" className="btn btn-primary" onClick={enregistrer} disabled={enregistrement}>{enregistrement ? t('catalog.common.saving') : t('catalog.common.save')}</button>
        <button type="button" className="btn btn-outline" onClick={demanderFermeture} disabled={enregistrement}>{t('catalog.common.cancel')}</button>
        {!nouveau && !historique && <button type="button" className="adm-btn-danger" onClick={supprimer} disabled={enregistrement || produit.active}>{t('catalog.drawer.delete')}</button>}
      </div>
      {!nouveau && !historique && produit.active && <p className="adm-note">{t('catalog.drawer.deleteNeedsInactive')}</p>}
    </Drawer>
  );
}
